import { useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Search, Link2, Loader2, ShieldCheck, IdCard } from "lucide-react";

/**
 * ABDMPatientDiscoveryModal — M1 patient discovery via ABHA / Health-ID.
 *
 * Two data sources, picked automatically by the backend:
 *   1. Local `patient_users` collection (fast path — patient already
 *      admitted here).
 *   2. Sandbox / gateway synthesis (returns a deterministic mock in
 *      sandbox mode; hits the ABDM gateway in production).
 *
 * When the record is NOT already linked to a local patient row, we
 * expose a **Link to local record** action that lets the receptionist
 * attach the ABHA id to any local `patient_id` — this is the entry
 * point to consent + record sharing for that patient.
 */
export default function ABDMPatientDiscoveryModal({ open, onClose, onLinked }) {
  const [healthId, setHealthId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [localPatientId, setLocalPatientId] = useState("");

  const reset = () => {
    setHealthId(""); setResult(null); setLocalPatientId("");
  };

  const search = async (e) => {
    e?.preventDefault?.();
    if (!healthId.trim()) return toast.error("Enter an ABHA / Health-ID");
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/abdm/patient/search",
        { health_id: healthId.trim() });
      setResult(data);
      if (data.linked) toast.success("Already linked locally");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Search failed");
    } finally { setBusy(false); }
  };

  const link = async () => {
    if (!result) return;
    const pid = localPatientId.trim() || result.id;
    if (!pid) {
      return toast.error("Enter the local patient id to link this ABHA record to");
    }
    setBusy(true);
    try {
      await api.post("/abdm/patient/link", {
        patient_id: pid, health_id: result.health_id || healthId.trim(),
        abha_number: result.abha_number,
      });
      toast.success("ABHA linked to local patient");
      onLinked?.({ patient_id: pid, health_id: result.health_id });
      reset(); onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Link failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg" data-testid="abdm-discovery-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <IdCard size={18} className="text-blue-600" />
            Search patient by ABHA / Health-ID
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={search} className="space-y-3">
          <div>
            <Label className="text-xs">ABHA address or Health-ID *</Label>
            <div className="flex gap-2 mt-1">
              <Input value={healthId} onChange={(e) => setHealthId(e.target.value)}
                     placeholder="e.g. ram@sbx  or  91-1234-5678-9012"
                     data-testid="abdm-discovery-input" />
              <Button type="submit" disabled={busy}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      data-testid="abdm-discovery-submit">
                {busy ? <Loader2 className="animate-spin" size={14} /> : <Search size={14} />}
              </Button>
            </div>
          </div>
        </form>

        {result && (
          <Card className="p-3 mt-3 border-blue-200 bg-blue-50/40"
                data-testid="abdm-discovery-result">
            <div className="flex justify-between items-start gap-3">
              <div className="text-sm">
                <p className="font-medium">{result.name || "—"}</p>
                <p className="text-xs text-slate-600">
                  ABHA: <span className="font-mono">{result.abha_number || "—"}</span>
                </p>
                {result.mobile && (
                  <p className="text-xs text-slate-600">Mobile: {result.mobile}</p>
                )}
                <p className="text-[10px] text-slate-500 mt-1">
                  Source: {result.source}
                  {result.sandbox_mode ? " · sandbox" : ""}
                  {result.linked && " · already linked"}
                </p>
              </div>
              {result.linked ? (
                <span className="px-2 py-1 text-[10px] rounded bg-emerald-100 text-emerald-800 flex items-center gap-1">
                  <ShieldCheck size={11} /> Linked
                </span>
              ) : (
                <span className="px-2 py-1 text-[10px] rounded bg-amber-100 text-amber-800">
                  Not linked
                </span>
              )}
            </div>
            {!result.linked && (
              <div className="mt-3 border-t border-blue-200 pt-3">
                <Label className="text-xs">Link to local patient id</Label>
                <Input value={localPatientId}
                       onChange={(e) => setLocalPatientId(e.target.value)}
                       placeholder={result.id || "paste patient id (or auto-fill if listed above)"}
                       className="mt-1 font-mono text-xs"
                       data-testid="abdm-discovery-link-pid" />
                <p className="text-[10px] text-slate-500 mt-1">
                  {result.id
                    ? "Auto-fills the local patient id we found above."
                    : "This ABHA has no matching local patient — enter the local id you want to link it to."}
                </p>
              </div>
            )}
          </Card>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>Close</Button>
          {result && !result.linked && (
            <Button onClick={link} disabled={busy}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="abdm-discovery-link-btn">
              <Link2 size={14} className="mr-1" /> Link to local record
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
